import { useState } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, CreditCard, Settings, LogOut, 
  Menu
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Logo } from '../../components/Logo';
import { useAuth } from '../../context/AuthContext';
import OnboardingModal from '../../components/OnboardingModal';

const navigation = [
  { name: 'Tableau de bord', href: '/client', icon: LayoutDashboard },
  { name: 'Mes devis', href: '/client/quotes', icon: FileText },
  { name: 'Abonnements', href: '/client/subscriptions', icon: CreditCard },
  { name: 'Factures', href: '/client/invoices', icon: FileText },
  { name: 'Paramètres', href: '/client/settings', icon: Settings },
];

export default function ClientLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, mustChangePassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user needs to complete onboarding
  const billing = user?.billing_info || {};
  const needsBilling = !billing.first_name || !billing.last_name || !billing.address || !billing.city || !billing.postal_code || !billing.phone;
  const showOnboarding = mustChangePassword || needsBilling;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mandatory Onboarding Modal - Cannot be closed */}
      {showOnboarding && <OnboardingModal />}

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-card border-r border-border transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border">
            <Link to="/" className="block">
              <Logo />
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    isActive 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.name}</span>
                  {item.href === '/client/settings' && mustChangePassword && (
                    <span className="ml-auto w-2 h-2 rounded-full bg-orange-500" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User info & logout */}
          <div className="p-4 border-t border-border">
            <div className="mb-4 px-4">
              <p className="text-sm font-medium truncate">{user?.email}</p>
              <p className="text-xs text-muted-foreground">Client</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
            >
              <LogOut className="h-5 w-5" />
              <span className="font-medium">Déconnexion</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile top bar */}
        <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-lg border-b border-border lg:hidden">
          <div className="flex items-center px-4 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-muted"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
